import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { runVettedImport, VETTED_QUERY, type ImportResult } from '../../services/talentCommunityApi';
import { Card } from './ui';

/**
 * The one-time Vetted Talent import from Lever (Oct 21 2026). A dry run first, which only counts;
 * the real run unlocks after it. Only the Talent Community leads may run either (the proxy checks).
 * Results are counts only — no names — so the number can be compared with Lever safely.
 */
const LABELS: [string, string][] = [
  ['opportunities', 'Lever opportunities archived as “Talent - GDPR Consented”'],
  ['noEmail', 'Left out: no email address in Lever'],
  ['consentEnded', 'Left out: archived more than 24 months ago, so consent has ended'],
  ['duplicatesMerged', 'Merged: the same person archived more than once'],
  ['people', 'People to import'],
  ['inProcess', '… of whom in a Lever hiring process right now (held back from campaigns)'],
  ['withCv', '… with a CV in Lever'],
  ['alreadyInTool', '… already in the tool (updated, not duplicated)'],
  ['written', 'Written to the tool'],
  ['cvFailed', 'CV could not be read'],
  ['processCheckFailed', 'Could not check for a current Lever process'],
  ['filteredOut', 'Returned by Lever with another reason (ignored)'],
];

function Counts({ result }: { result: ImportResult }) {
  return (
    <dl className="divide-y divide-solid divide-zinc-100 text-sm">
      {LABELS.filter(([k]) => (result.counts[k] ?? 0) > 0 || ['opportunities', 'people'].includes(k)).map(([k, label]) => (
        <div key={k} className="flex justify-between gap-4 py-2">
          <dt className={k === 'people' || k === 'written' ? 'font-medium text-zinc-900' : 'text-zinc-600'}>{label}</dt>
          <dd className="font-semibold tabular-nums text-zinc-900">{result.counts[k] ?? 0}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ImportTab() {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<'dry' | 'real' | null>(null);
  const [dry, setDry] = useState<ImportResult | null>(null);
  const [real, setReal] = useState<ImportResult | null>(null);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);

  async function run(dryRun: boolean) {
    setBusy(dryRun ? 'dry' : 'real');
    setError('');
    try {
      const result = await runVettedImport(dryRun);
      if (dryRun) setDry(result);
      else {
        setReal(result);
        await queryClient.invalidateQueries({ queryKey: VETTED_QUERY.queryKey });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The import failed');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-5">
      <Card title="Import Vetted Talent from Lever">
        <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-zinc-700">
          <li>Everyone archived in Lever as “Talent - GDPR Consented”, read with the Talent Community’s own Lever key.</li>
          <li>Stored: name, email, role, stage reached, location, archive date, Lever link and the CV as Lever parsed it.</li>
          <li>Not stored: interview notes and scorecards. They stay in Lever.</li>
          <li>Consent runs 24 months from the archive date. 1:1 job matches only, no newsletter, until they choose otherwise.</li>
          <li>Safe to run again: it updates the same people and never creates duplicates.</li>
        </ul>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" loading={busy === 'dry'} disabled={busy !== null} onClick={() => run(true)}>
            1. Dry run (counts only)
          </Button>
          <Button size="sm" loading={busy === 'real'} disabled={busy !== null || !dry || (dry.counts.people ?? 0) === 0} onClick={() => setConfirming(true)}>
            2. Import {dry ? dry.counts.people ?? 0 : ''} people
          </Button>
        </div>
        {busy && <p className="mt-3 text-sm text-zinc-500">Reading Lever. This takes about a minute…</p>}
        {error && (
          <p role="alert" className="mt-3 text-sm text-red-700">
            {error}
          </p>
        )}
      </Card>

      {dry && (
        <Card title="Dry run — nothing was written">
          <Counts result={dry} />
          {dry.lastError && <p className="mt-3 text-sm text-amber-800">{dry.lastError}</p>}
        </Card>
      )}

      {real && (
        <Card title="Import done">
          <Counts result={real} />
          {real.lastError && <p className="mt-3 text-sm text-amber-800">{real.lastError}</p>}
        </Card>
      )}

      {confirming && dry && (
        <Modal open onOpenChange={(next: boolean) => !next && setConfirming(false)}>
          <ModalContent className="!h-max !w-[min(100vw-2rem,30rem)] gap-4 rounded p-6">
            <ModalHeader>
              <ModalTitle>Import {dry.counts.people ?? 0} people?</ModalTitle>
            </ModalHeader>
            <p className="text-sm text-zinc-700">
              Their names, emails, roles and CVs will be stored in the Talent Community and visible to the TA team.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => {
                  setConfirming(false);
                  void run(false);
                }}
              >
                Yes, import
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
}
